import i18n, { PostProcessorModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-xhr-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// eslint-disable-next-line import/no-unresolved,import/no-webpack-loader-syntax
import languageMap from '@amzn/katal-localization/webpack-loader!';

// Whether initialization is complete
let isInitialized = false;
// If initialization is complete, the last error, if any
let initializationError: string | undefined;

type I18nCallback = (err?: string) => void;
// Registered callbacks for when i18n initialization completes
const i18nInitializationCallbacks: I18nCallback[] = [];

const stringDebug: PostProcessorModule = {
  name: 'stringDebugger',
  process(value, key) {
    const url = window.location.href;
    if (url.includes('stringDebug')) {
      return `${value}[${key}]`;
    }
    return value;
  },
  type: 'postProcessor',
};

i18n.use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(stringDebug)
  .init(
    {
      backend: {
        crossDomain: true,
        loadPath: (localeList: any, ns: string | string[]) => {
          const locale = localeList[0];
          const currentNS: string = Array.isArray(ns) ? ns[0] : ns;
          if (locale.includes('fallback_translation')) {
            return languageMap[currentNS];
          }
          return languageMap[`${currentNS}-${[localeList[0]]}`];
        },
      },
      debug: process.env.NODE_ENV !== 'production',
      defaultNS: 'rav_shared_reporting_components',
      detection: {
        lookupQuerystring: 'language',
        // order and from where user language should be detected
        order: ['querystring', 'navigator'],
      },
      fallbackLng: {
        default: ['en-US'],
        'es-US': ['es-ES'],
        'fr-CA': ['fr-fr'],
      },
      fallbackNS: [
        'pnf_dispute',
        'pnf_header_footer',
        'pnf_language_selection',
        'pnf_retract',
        'pnf_website_components',
      ],
      interpolation: {
        // not needed for react as it escapes by default
        escapeValue: false,
      },
      load: 'currentOnly',
      ns: [
        'pnf_dispute',
        'pnf_header_footer',
        'pnf_language_selection',
        'pnf_retract',
        'pnf_website_components',
      ],
      postProcess: ['stringDebugger'],
    },
    (err?: string) => {
      initializationError = err;
      isInitialized = true;
      i18nInitializationCallbacks.forEach((callback) => {
        callback(initializationError);
      });
      i18nInitializationCallbacks.length = 0;
    }
  );

i18n.on('languageChanged', () => {
  // We must manually translate the title of the page, unless this is
  // a Mons app, in which case the title is handled by Seller Central.
  const titleEl = document.querySelector('#app-title') as HTMLElement;
  if (titleEl) {
    titleEl.innerText = i18n.t('public-notice-form-report-page-title');
  }
});

/**
 * Helper function to run the given callback to report the status of translation initialization.
 *
 * If translation is already initialized the callback will run immediately, otherwise it will run
 * when translation initialization is complete.
 *
 * This helper function fills a gap in the i18next library, where it doesn't report the initialization
 * status through any of its own events or status flags.
 */
const i18nWhenReady = (callback: I18nCallback) => {
  if (isInitialized) {
    callback(initializationError);
  } else {
    i18nInitializationCallbacks.push(callback);
  }
};

export { i18nWhenReady };
export default i18n;
